import { CdkDrag } from "@angular/cdk/drag-drop";
import {
  AfterContentInit,
  ContentChildren,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
} from "@angular/core";
import { Subscription } from "rxjs";

@Directive({
  selector: "[appCdkDropListAutoScroll]",
  standalone: false,
})
export class CdkDropListAutoScrollDirective implements AfterContentInit, OnDestroy {
  private static readonly EDGE_ZONE_PX = 80;
  private static readonly MAX_SCROLL_SPEED = 15;

  @ContentChildren(CdkDrag, { descendants: true }) private dragItems: QueryList<CdkDrag>;

  private scrollElement: HTMLElement | null = null;
  private autoScrollAnimationId: number | null = null;
  private autoScrollSpeed = 0;
  private isDragging = false;
  private dragSubscriptions: Subscription[] = [];
  private queryListSubscription: Subscription | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {}

  ngAfterContentInit(): void {
    this.subscribeToDragItems();
    this.queryListSubscription = this.dragItems.changes.subscribe(() => {
      this.unsubscribeFromDragItems();
      this.subscribeToDragItems();
    });
  }

  ngOnDestroy(): void {
    this.queryListSubscription?.unsubscribe();
    this.unsubscribeFromDragItems();
    this.stopAutoScroll();
  }

  private subscribeToDragItems(): void {
    this.dragItems.forEach((drag) => {
      this.dragSubscriptions.push(
        drag.started.subscribe(() => this.onDragStarted()),
        drag.moved.subscribe((event) => this.onDragMoved(event.pointerPosition.y)),
        drag.ended.subscribe(() => this.onDragEnded()),
      );
    });
  }

  private unsubscribeFromDragItems(): void {
    this.dragSubscriptions.forEach((sub) => sub.unsubscribe());
    this.dragSubscriptions = [];
  }

  private async onDragStarted(): Promise<void> {
    this.isDragging = true;
    if (!this.scrollElement) {
      this.scrollElement = await this.resolveScrollElement();
    }
  }

  private onDragMoved(pointerY: number): void {
    if (!this.scrollElement) {
      return;
    }

    const rect = this.scrollElement.getBoundingClientRect();
    const distanceFromTop = pointerY - rect.top;
    const distanceFromBottom = rect.bottom - pointerY;
    const edgeZone = CdkDropListAutoScrollDirective.EDGE_ZONE_PX;
    const maxSpeed = CdkDropListAutoScrollDirective.MAX_SCROLL_SPEED;

    if (distanceFromTop < edgeZone && distanceFromTop >= 0) {
      const ratio = 1 - distanceFromTop / edgeZone;
      this.autoScrollSpeed = -Math.ceil(ratio * maxSpeed);
      this.startAutoScroll();
    } else if (distanceFromBottom < edgeZone && distanceFromBottom >= 0) {
      const ratio = 1 - distanceFromBottom / edgeZone;
      this.autoScrollSpeed = Math.ceil(ratio * maxSpeed);
      this.startAutoScroll();
    } else {
      this.autoScrollSpeed = 0;
      this.stopAutoScroll();
    }
  }

  private onDragEnded(): void {
    this.isDragging = false;
    this.stopAutoScroll();
  }

  private startAutoScroll(): void {
    if (this.autoScrollAnimationId !== null) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const scrollLoop = () => {
        if (!this.isDragging || this.autoScrollSpeed === 0 || !this.scrollElement) {
          this.autoScrollAnimationId = null;
          return;
        }
        this.scrollElement.scrollTop += this.autoScrollSpeed;
        this.autoScrollAnimationId = requestAnimationFrame(scrollLoop);
      };
      this.autoScrollAnimationId = requestAnimationFrame(scrollLoop);
    });
  }

  private stopAutoScroll(): void {
    if (this.autoScrollAnimationId !== null) {
      cancelAnimationFrame(this.autoScrollAnimationId);
      this.autoScrollAnimationId = null;
    }
    this.autoScrollSpeed = 0;
  }

  private async resolveScrollElement(): Promise<HTMLElement | null> {
    let current: HTMLElement | null = this.el.nativeElement;
    while (current) {
      if (current.tagName === "ION-CONTENT") {
        return (current as any).getScrollElement();
      }
      current = current.parentElement;
    }
    return null;
  }
}
